import * as React from 'react';
import { EErrorCode } from 'config/errors-codes';
import { errorCollection } from 'config/ui/error-collection';
import type { IBrand, IMarket } from 'data/schema';
import type { AwsSecret } from '@org-avp/models';
import { useError } from 'hooks/useError';
import {
  useBandwidthOptimisation,
  BandwidthOptimisationConfig as BandwidthOptimisationConfig,
} from 'hooks/useBandwidthOptimisation';
import { useLocalAveDeviceSwitch } from './useLocalAveDeviceSwitch';
import { updateSessionAndEmitAnalyticsEvent, userSession } from 'state/session';
import { showToast } from 'state/toast';
import { user } from 'utils/auth/user';
import logger from 'utils/log';
import { IntegrationType, integrationType, notifyElementLifecycle } from 'utils/ui/app';
import { getShowroomConfig, injectShowroom } from 'utils/ui/showroom';
import { getStreamingStage, StreamingStage } from 'utils/ui/stage';

export type DeckAttributes = React.HTMLAttributes<HTMLElement> & {
  active?: boolean;
  'pr-string'?: string;
  'camera-flight'?: 'disabled' | 'enabled';
  'config-id': string;
  'hide-consumption-data'?: boolean;
  camera?: string;
  market: string;
  style?: React.CSSProperties | string;
  ref?: React.RefObject<HTMLElement | null>;
  features?: string;
  'streaming-stage'?: StreamingStage;
};

export type IShowroomProps = {
  market: string;
  brand: IBrand;
  active?: boolean;
  prString?: string;
  hideConsumptionData?: boolean;
  camera?: string;
  cameraFlight?: 'enabled' | 'disabled';
  sessionId?: string;
  className?: string;
  userType?: 'dealer' | 'customer';
  customerAllowance?: boolean;
  features?: Partial<IShowroomFeatureFlags>;
  bandwidthOptimisation?: BandwidthOptimisationConfig & {
    enabled?: boolean;
  };
  suppressErrorToast?: boolean;
  onConnected?: () => void;
  onError?: (event: IShowroomErrorEvent, showroomErrorData: Record<string, unknown>) => boolean | void;
  onActiveChange?: (active: boolean) => void;
  /**
   * `deckPrString` is the configuration the deck had loaded when it computed `wltpText`, so the
   * consumer can bind the write to that car rather than to whatever became active meanwhile
   * (AVS-4400/AVS-4421).
   */
  onUpdateConsumptionData?: (wltpText?: string, deckPrString?: string) => void;
  onUpdatePrString?: (prString?: string) => void;
  onTimeout?: () => void;
  onLocalAveOverride?: () => void;
  onLocalAveSwitching?: (switching: boolean) => void;
  credentialsProvider?: () => Promise<AwsSecret>;
  localAveName?: string;
  localAveClientId?: string;
};

export interface IShowroomState {
  active: boolean;
  sessionId?: string;
}

// This type is extracted from Showroom directly
export type IShowroomErrorEvent = {
  type: 'exception' | 'disconnected';
  message: string;
  error: Error;
};

export interface IShowroomFeatureFlags {
  close?: boolean;
  cameraNavigation?: boolean;
  animations?: boolean;
  fullscreen?: boolean;
  cameraFlight?: boolean;
  animationSpots?: boolean;
  interior?: boolean;
  environments?: boolean;
  zoom?: boolean;
}

export interface IShowroomElement extends HTMLElement {
  setConnection: (
    config: { clientId: string; channelName: string; credentialsProvider: () => Promise<AwsSecret> } | null
  ) => void;
}

export function Showroom(props: IShowroomProps) {
  const {
    active = true,
    market,
    brand,
    prString,
    hideConsumptionData,
    camera,
    cameraFlight = 'disabled',
    sessionId,
    className = '',
    userType = user.type,
    suppressErrorToast = false,
    customerAllowance = false,
    features,
    bandwidthOptimisation = { enabled: false },
    onConnected,
    onError,
    onUpdateConsumptionData,
    onUpdatePrString,
    onTimeout,
    onLocalAveOverride,
    onLocalAveSwitching,
    credentialsProvider,
    localAveName,
    localAveClientId,
  } = props;

  const showroom = React.useRef<HTMLElement>(null);
  const hasListeners = React.useRef(false);
  /**
   * The configuration the DECK currently has, which is not the same thing as the `prString` prop:
   * on a car switch the prop moves immediately while the deck is still rendering — and still
   * reporting consumption data for — the previous one. Seeded from the prop at mount, then owned
   * by the deck's own `update:pr-string` announcements (AVS-4400).
   */
  const deckPrString = React.useRef(prString);
  const [scriptLoaded, setScriptLoaded] = React.useState(false);
  const throwError = useError();
  // Keep suppressErrorToast in a ref so the listener can access the current value
  const suppressErrorToastRef = React.useRef(suppressErrorToast);
  /**
   * AVS-4411: aborts every deck listener when this mount goes away. A 2D<->3D toggle fully
   * unmounts and remounts Showroom (see `View.tsx`), and without this the detached element from
   * the previous generation stays wired to the previous render's `onError` — `onAveError`, which
   * forces `activeExperience: '2D'` on the singleton session. A teardown event arriving from the
   * old deck after the dealer has already returned to 3D then drags the whole session back to 2D.
   */
  const listenersAbortRef = React.useRef<AbortController | null>(null);
  const onErrorRef = React.useRef(onError);
  const credentialsProviderRef = React.useRef(credentialsProvider);
  const localAveNameRef = React.useRef(localAveName);
  const localAveClientIdRef = React.useRef(localAveClientId);

  // Update refs on each render to avoid stale closure in event listeners
  React.useEffect(() => {
    suppressErrorToastRef.current = suppressErrorToast;
    onErrorRef.current = onError;
    credentialsProviderRef.current = credentialsProvider;
    localAveNameRef.current = localAveName;
    localAveClientIdRef.current = localAveClientId;
  }, [suppressErrorToast, onError, credentialsProvider, localAveName, localAveClientId]);

  const bandwidthState = useBandwidthOptimisation({
    enabled: bandwidthOptimisation.enabled,
    ...bandwidthOptimisation,
  });

  // AVS-4389: real hardware showed the deck drops its parked signaling config on deactivation, so
  // it must be re-applied before the switch hook reactivates or the deck falls back to cloud
  // streaming. Mirrors the setConnection effect below exactly, reading live values through refs
  // since this callback's identity must stay stable for the hook.
  const reissueLocalAveConnection = React.useCallback(() => {
    const element = showroom.current as IShowroomElement | undefined;
    const currentCredentialsProvider = credentialsProviderRef.current;
    const currentLocalAveName = localAveNameRef.current;
    if (!element?.setConnection || !currentCredentialsProvider || !currentLocalAveName) return;

    element.setConnection({
      clientId: localAveClientIdRef.current ?? `avp-3dexhibition-${crypto.randomUUID()}`,
      channelName: currentLocalAveName,
      credentialsProvider: currentCredentialsProvider,
    });
  }, []);

  // PR review (AVS-4389): the new device never confirmed within the switch window — surface it
  // the same way as any other stream failure instead of leaving a dead canvas with no signal.
  const handleSwitchTimedOut = React.useCallback(() => {
    if (!suppressErrorToastRef.current) {
      showToast({
        type: 'warn',
        title: errorCollection[EErrorCode.LOCAL_AVE_SWITCH_TIMEOUT].title(),
        message: errorCollection[EErrorCode.LOCAL_AVE_SWITCH_TIMEOUT].message(),
        notificationId: errorCollection[EErrorCode.LOCAL_AVE_SWITCH_TIMEOUT].notificationId,
      });
    }
    onErrorRef.current?.(
      {
        type: 'exception',
        message: 'Local AVE device switch timed out',
        error: new Error('Local AVE device switch timed out'),
      },
      { reason: 'LOCAL_AVE_SWITCH_TIMEOUT' }
    );
  }, []);

  // AVS-4389: independent channel of logic — restarts the deck's stream on an authorised local
  // AVE device switch. ANDed onto shouldBeActive below so bandwidth optimisation still wins.
  const deviceSwitch = useLocalAveDeviceSwitch(localAveName, reissueLocalAveConnection, handleSwitchTimedOut);

  const shouldBeActive =
    (bandwidthState.isManaged ? active && bandwidthState.shouldBeActive : active) && !deviceSwitch.forceInactive;

  // AVS-4389: surfaces switchInFlight to View.tsx via callback (mirrors onLocalAveOverride) —
  // Showroom stays a dumb leaf and never reaches up into container-level UI itself.
  React.useEffect(() => {
    onLocalAveSwitching?.(deviceSwitch.switchInFlight);
  }, [deviceSwitch.switchInFlight, onLocalAveSwitching]);

  const featureBlackListCustomer: IShowroomFeatureFlags = {
    close: false,
    cameraNavigation: customerAllowance,
    animations: customerAllowance,
    fullscreen: customerAllowance,
    cameraFlight: customerAllowance,
    animationSpots: customerAllowance,
    interior: customerAllowance,
    environments: customerAllowance,
    zoom: customerAllowance,
  };

  const featureBlacklist = {
    dealer: {
      close: false,
    },
    customer: featureBlackListCustomer,
  };

  React.useEffect(() => {
    if (showroom.current && !hasListeners.current) {
      hasListeners.current = true;
      const controller = new AbortController();
      listenersAbortRef.current = controller;
      const { signal } = controller;
      const on = (type: string, handler: (e: Event) => void) =>
        showroom.current?.addEventListener(type, handler, { signal });

      on('connected', (e) => {
        /**
         * When we connect to a cloud streaming instance, we receive an `aveSessionId` as a unique identifier
         * When we connect to a local AVE, we receive an `aveChannelName` instead, which is the id of the local AVE in use.
         * The main purpose of the aveSessionId is to enabled multisessions with customers. The aveChannelName does not provide that utility, and mostly serves as a proof that the connection to the local AVE has been established.
         */
        const aveSessionId = (e as CustomEvent).detail.sessionId;
        const aveChannelName = (e as CustomEvent).detail.channelName;
        if (!sessionId) {
          logger.warn(
            `Starting new streaming session without existing sessionId (received ${aveSessionId ? 'sessionId' : 'channelName'} from AVE).`
          );
          // Use updateSessionAndEmitAnalyticsEvent so that analytics captures mmToken
          // (aveSessionId) in a SESSION_UPDATED event for unshared 3D sessions.
          if (aveSessionId) updateSessionAndEmitAnalyticsEvent({ aveSessionId, streamActive: true });
          if (aveChannelName) updateSessionAndEmitAnalyticsEvent({ aveChannelName, streamActive: true });
        }
        if (aveChannelName) {
          // Defense-in-depth: clear takeover marker on fresh local-AVE connection.
          // share() already clears it synchronously, but passive/observer tabs benefit here too.
          userSession().setValues({ localAveTakenOver: false });
        }
        // AVS-4389: ends the long switchInFlight window once the new device has handshaked.
        deviceSwitch.notifyDeckConnected();
        onConnected?.();
      });

      on('disconnected', (e) => {
        // AVS-4389: a deliberate active=false teardown, mid-switch, must never reach the
        // false-takeover dialog or the generic toast/onError->forced-2D path below.
        if (deviceSwitch.switchInFlightRef.current) {
          deviceSwitch.notifyDeckTornDown();
          return;
        }

        /** Don't end the session when the stream is just hidden */
        if (bandwidthState.isManaged && active) {
          return;
        }
        const reason = (e as CustomEvent).detail.reason as string | undefined;

        if (userSession().isConnectedToLocalAve) {
          logger.warn('Local AVE session disconnected — device was likely claimed by another user.');
          onLocalAveOverride?.();
          return;
        }

        // Unexpected 3DWS disconnect — notify consumer and potentially fall back.
        logger.warn(`3DWS stream disconnected: ${reason ?? 'no reason given'}`);

        // onTimeout is a lifecycle callback — fire unconditionally regardless of
        // whether toast suppression is active.
        if (reason === 'INACTIVITY_TIMEOUT') {
          onTimeout?.();
        }

        if (!suppressErrorToastRef.current) {
          if (reason === 'INACTIVITY_TIMEOUT') {
            showToast({
              type: 'warn',
              title: errorCollection[EErrorCode.STREAM_TIMEOUT].title(),
              message: errorCollection[EErrorCode.STREAM_TIMEOUT].message(),
              notificationId: errorCollection[EErrorCode.STREAM_TIMEOUT].notificationId,
            });
          } else {
            showToast({
              type: 'warn',
              title: errorCollection[EErrorCode.STREAM_DISCONNECTED].title(),
              message: errorCollection[EErrorCode.STREAM_DISCONNECTED].message(),
              notificationId: errorCollection[EErrorCode.STREAM_DISCONNECTED].notificationId,
            });
          }
        }

        onErrorRef.current?.(
          {
            type: 'disconnected',
            message: reason ?? 'Stream disconnected',
            error: new Error(reason ?? 'Stream disconnected'),
          },
          { reason }
        );
      });

      on('exception', (e) => {
        // AVS-4389: mirrors the 'disconnected' guard above — the vendor deck may emit
        // 'exception' instead of (or alongside) 'disconnected' on a deliberate teardown.
        if (deviceSwitch.switchInFlightRef.current) {
          deviceSwitch.notifyDeckTornDown();
          return;
        }

        const errorCode = processShowroomError(e as CustomEvent, suppressErrorToastRef.current);
        if (errorCode) {
          handleStreamError(
            e as unknown as IShowroomErrorEvent,
            errorCollection[errorCode],
            onErrorRef.current,
            throwError
          );
        }
      });

      on('update:consumption-data', (e) => {
        onUpdateConsumptionData?.((e as CustomEvent).detail?.value, deckPrString.current);
      });

      on('update:pr-string', (e) => {
        const value = (e as CustomEvent).detail?.value;
        if (value) deckPrString.current = value;
        onUpdatePrString?.(value);
      });
    }

    // These dependencies are enough as we don't want to update events unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showroom, onConnected, scriptLoaded]);

  /**
   * AVS-4411: unmount-only teardown for the listeners attached above.
   *
   * Deliberately its own effect with an empty dependency list — `onConnected` is re-created on
   * every render of `View.tsx`, so aborting from the attach effect's own cleanup would detach and
   * re-attach every listener on every render and drop any event landing in that gap.
   *
   * `useLayoutEffect`, not `useEffect`, for the same reason AVS-4389 reasons about commit phases:
   * a passive cleanup runs AFTER React has already detached the host node, so a deck that
   * dispatches `disconnected`/`exception` synchronously from `disconnectedCallback` would still
   * reach a live listener. A layout cleanup runs in the commit phase, before the `<avp-3dws-deck>`
   * host node below this fiber is removed, which closes that window too.
   *
   * The reset of `hasListeners` is what lets the NEXT mount re-attach — every 2D<->3D toggle
   * remounts this component, and DCC remounts it again on its own reinit path.
   */
  React.useLayoutEffect(() => {
    return () => {
      listenersAbortRef.current?.abort();
      listenersAbortRef.current = null;
      hasListeners.current = false;
    };
  }, []);

  React.useEffect(() => {
    const element = showroom.current as IShowroomElement | undefined;
    if (!element?.setConnection) return;

    if (credentialsProvider && localAveName) {
      element.setConnection({
        clientId: localAveClientId ?? `avp-3dexhibition-${crypto.randomUUID()}`,
        channelName: localAveName,
        credentialsProvider,
      });
    } else {
      element.setConnection(null);
    }

    return () => {
      element.setConnection(null);
    };
  }, [credentialsProvider, localAveName, localAveClientId, scriptLoaded]);

  React.useEffect(() => {
    injectShowroom({ onLoad: setScriptLoaded });
  }, []);

  // AVS-4433: the deck's mount/unmount is the one element lifecycle observable from inside React,
  // and it is how a DCC `<e3d-stream>` remount becomes visible — DCC calls `__e3dReinitCallback`
  // after its OIDC login and after every 401 refresh, which replaces the element and tears this
  // React root down with it. Reporting only, through the zero-import seam in `utils/ui/app`: no
  // decision is taken here and the sink's return value does not exist, so this cannot change what
  // the deck does. `scriptLoaded` is the dependency because the element does not exist before it.
  React.useEffect(() => {
    const element = showroom.current;

    if (!element) {
      return;
    }

    notifyElementLifecycle('deck-mounted', element);

    return () => notifyElementLifecycle('deck-unmounted', element);
  }, [scriptLoaded]);

  if (!scriptLoaded) {
    return null;
  }

  const isScheduledSession = !!userSession().get('scheduledStartTime');

  return (
    <avp-3dws-deck
      part="showroom"
      role="presentation"
      config-id={getShowroomConfig(brand, isScheduledSession)}
      active={shouldBeActive}
      hide-consumption-data={hideConsumptionData}
      market={(market === 'DEV' ? 'DEU' : market) as IMarket}
      pr-string={prString}
      camera={camera}
      camera-flight={cameraFlight}
      session-id={sessionId}
      /** Since this is a web component, the correct attribute is class, not className */
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      class={className}
      ref={showroom}
      features={JSON.stringify({ ...featureBlacklist[userType], ...features })}
      streaming-stage={getStreamingStage(brand)}
    />
  );
}

function processShowroomError(
  error: CustomEvent<{ reason?: string; message?: string }>,
  suppressToast = false
): EErrorCode | undefined {
  try {
    const errorCode = error.detail?.reason as keyof typeof EErrorCode;
    const errorMessage = error.detail?.message || '';

    const errorNumber = errorCode && EErrorCode[errorCode];

    if (errorNumber) {
      if (!suppressToast) {
        showToast({
          type: 'error',
          title: errorCollection[errorNumber].title(),
          message: errorCollection[errorNumber].message(),
          notificationId: errorCollection[errorNumber].notificationId,
        });
      }
      return errorNumber;
    }

    if (errorMessage) {
      if (!suppressToast) {
        showToast({
          type: 'error',
          title: errorCollection[EErrorCode.GENERAL_STREAM_ERROR].title(),
          message: errorMessage,
          notificationId: errorCollection[EErrorCode.GENERAL_STREAM_ERROR].notificationId,
        });
      }
      return EErrorCode.GENERAL_STREAM_ERROR;
    }

    if (!suppressToast) {
      showToast({
        type: 'error',
        title: errorCollection[EErrorCode.GENERAL_STREAM_ERROR].title(),
        message: errorCollection[EErrorCode.GENERAL_STREAM_ERROR].message(),
        notificationId: errorCollection[EErrorCode.GENERAL_STREAM_ERROR].notificationId,
      });
    }

    return EErrorCode.GENERAL_STREAM_ERROR;
  } catch {
    return EErrorCode.GENERAL_STREAM_ERROR;
  }
}

function handleStreamError(
  errorEvent: IShowroomErrorEvent,
  errorData: Record<string, unknown>,
  onError: IShowroomProps['onError'],
  throwError: (code: EErrorCode, options?: { message?: string }) => void
): void {
  let shouldShowErrorBoundary = true;
  if (onError) {
    shouldShowErrorBoundary = onError(errorEvent, errorData) !== false;
  }
  if (!shouldShowErrorBoundary && integrationType() !== IntegrationType.WebComponent) {
    return;
  }
  if (!shouldShowErrorBoundary) {
    throwError(EErrorCode.INTERNAL_ERROR, { message: errorEvent.message });
  }
}
